// apps/web/src/pages/MechanicGaragesPublicPage.tsx

import React, { useState } from 'react';
import { trpc } from '@/trpc/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { 
  Wrench, Search, MapPin, Phone, Mail, Globe, Clock, 
  ChevronLeft, ChevronRight, Terminal, Info as InfoIcon,
  Star, Navigation
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { District } from '@logistics-platform/types';
import { toast } from 'sonner';

interface MechanicGarage {
  id: string;
  name: string;
  phone: string;
  phoneTwo?: string | null;
  email?: string | null;
  address?: string | null;
  district: District;
  latitude?: number | null;
  longitude?: number | null;
  isVerified: boolean;
  services: string[];
  specialties: string[];
  description?: string | null;
  website?: string | null;
  workingHours?: string | null;
}

const MechanicGaragesPublicPage: React.FC = () => {
  const [page, setPage] = useState(1);
  const pageSize = 12;
  const [selectedDistrict, setSelectedDistrict] = useState<District | undefined>(undefined);
  const [selectedSpecialty, setSelectedSpecialty] = useState<string | undefined>(undefined);

  const { data: districts } = trpc.emergencyService.getDistricts.useQuery();
  
  const { data, isLoading, error } = trpc.emergencyService.getPublicMechanicGarages.useQuery({
    district: selectedDistrict,
    specialty: selectedSpecialty,
    page,
    pageSize,
  }, {
    refetchInterval: 30000 // Refresh every 30 seconds
  });

  const garages = data?.garages || [];
  const totalGarages = data?.totalCount || 0;
  const totalPages = Math.ceil(totalGarages / pageSize);

  const availableSpecialties = [
    { code: 'HEAVY_DUTY', label: 'Heavy Duty Vehicles' },
    { code: 'LIGHT_VEHICLE', label: 'Light Vehicles' },
    { code: 'MOTORCYCLE', label: 'Motorcycles' },
    { code: 'ELECTRICAL', label: 'Electrical Systems' },
    { code: 'HYBRID_ELECTRIC', label: 'Hybrid & Electric' },
    { code: 'DIESEL_ENGINE', label: 'Diesel Engines' },
    { code: 'IMPORT_CARS', label: 'Import Cars' },
    { code: 'FLEET_SERVICE', label: 'Fleet Service' },
  ];

  const availableServices = [
    { code: 'ENGINE_REPAIR', label: 'Engine Repair' },
    { code: 'BRAKE_SERVICE', label: 'Brake Service' },
    { code: 'OIL_CHANGE', label: 'Oil Change' },
    { code: 'TRANSMISSION_REPAIR', label: 'Transmission' },
    { code: 'AIR_CONDITIONING', label: 'A/C Service' },
    { code: 'ELECTRICAL_REPAIR', label: 'Electrical' },
    { code: 'SUSPENSION_WORK', label: 'Suspension' },
    { code: 'TIRE_SERVICES', label: 'Tire Services' },
    { code: 'DIAGNOSTICS', label: 'Diagnostics' },
    { code: 'BATTERY_REPLACEMENT', label: 'Batteries' },
  ];

  const getSpecialtyLabel = (code: string) => availableSpecialties.find(s => s.code === code)?.label || code;
  const getServiceLabel = (code: string) => availableServices.find(s => s.code === code)?.label || code;

  const handleCall = (phoneNumber: string) => {
    window.location.href = `tel:${phoneNumber}`;
  };

  const handleGetDirections = (garage: MechanicGarage) => {
    if (garage.latitude && garage.longitude) {
      window.open(`https://www.google.com/maps/dir/?api=1&destination=${garage.latitude},${garage.longitude}`, '_blank');
    } else if (garage.address) {
      window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(garage.address + ', ' + garage.district)}`, '_blank');
    } else {
      toast.error("No location information available for this garage");
    }
  };

  if (error) {
    return (
      <div className="container mx-auto py-12 px-4 max-w-7xl">
        <Alert variant="destructive">
          <Terminal className="h-4 w-4" />
          <AlertTitle>Error Loading Mechanic Garages</AlertTitle>
          <AlertDescription>{error.message}</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/20">
      {/* Hero Section */}
      <div className="bg-primary text-primary-foreground py-12 md:py-16">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="text-center space-y-4">
            <div className="flex items-center justify-center gap-3">
              <Wrench className="h-10 w-10" />
              <h1 className="text-3xl md:text-4xl font-bold">Registered Mechanic Garages</h1>
            </div>
            <p className="text-primary-foreground/80 max-w-2xl mx-auto text-lg">
              Find verified mechanic garages across Malawi. All garages listed here are registered and verified by our platform.
            </p>
          </div>
        </div>
      </div>

      <div className="container mx-auto py-8 px-4 max-w-7xl">
        {/* Filters */}
        <Card className="mb-8">
          <CardContent className="p-4 md:p-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <label className="text-sm font-medium mb-2 block">Filter by District</label>
                <Select value={selectedDistrict} onValueChange={(value: District) => { setSelectedDistrict(value); setPage(1); }}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="All Districts" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={undefined as any}>All Districts</SelectItem>
                    {districts?.map(district => <SelectItem key={district} value={district}>{district}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex-1">
                <label className="text-sm font-medium mb-2 block">Filter by Specialty</label>
                <Select value={selectedSpecialty} onValueChange={(value) => { setSelectedSpecialty(value); setPage(1); }}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="All Specialties" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={undefined as any}>All Specialties</SelectItem>
                    {availableSpecialties.map(specialty => (
                      <SelectItem key={specialty.code} value={specialty.code}>{specialty.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end">
                <Button 
                  variant="outline" 
                  onClick={() => { setSelectedDistrict(undefined); setSelectedSpecialty(undefined); setPage(1); }}
                  className="w-full md:w-auto"
                >
                  Reset Filters
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Results Count */}
        <div className="mb-4 flex items-center justify-between">
          <p className="text-muted-foreground">
            Showing {garages.length} of {totalGarages} garages
          </p>
          {selectedDistrict && (
            <Badge variant="secondary">{selectedDistrict}</Badge>
          )}
        </div>

        {/* Garages Grid */}
        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({ length: pageSize }).map((_, i) => (
              <Card key={i} className="h-64">
                <CardContent className="p-6 space-y-4">
                  <Skeleton className="h-6 w-3/4" />
                  <Skeleton className="h-4 w-1/2" />
                  <Skeleton className="h-4 w-2/3" />
                  <Skeleton className="h-20 w-full" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : garages.length === 0 ? (
          <Card className="py-16">
            <CardContent className="text-center">
              <InfoIcon className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">No garages found</h3>
              <p className="text-muted-foreground max-w-md mx-auto">
                No mechanic garages match your current filters. Try adjusting your search criteria or check back later.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {garages.map((garage) => (
              <Card key={garage.id} className="overflow-hidden hover:shadow-lg transition-shadow">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="text-lg flex items-center gap-2">
                        {garage.name}
                        {garage.isVerified && (
                          <Badge variant="success" className="text-xs">
                            <Star className="h-3 w-3 mr-1" /> Verified
                          </Badge>
                        )}
                      </CardTitle>
                      <CardDescription className="flex items-center gap-1 mt-1">
                        <MapPin className="h-3 w-3" />
                        {garage.district}
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Description */}
                  {garage.description && (
                    <p className="text-sm text-muted-foreground line-clamp-2">{garage.description}</p>
                  )}

                  {/* Specialties */}
                  {garage.specialties.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {garage.specialties.slice(0, 3).map(specialty => (
                        <Badge key={specialty} variant="secondary" className="text-xs">
                          {getSpecialtyLabel(specialty)}
                        </Badge>
                      ))}
                      {garage.specialties.length > 3 && (
                        <Badge variant="outline" className="text-xs">
                          +{garage.specialties.length - 3} more
                        </Badge>
                      )}
                    </div>
                  )}

                  {/* Services */}
                  {garage.services.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {garage.services.slice(0, 4).map(service => (
                        <span key={service} className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded">
                          {getServiceLabel(service)}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Contact Info */}
                  <div className="space-y-2 text-sm pt-2 border-t">
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Phone className="h-4 w-4" />
                      <span>{garage.phone}</span>
                    </div>
                    {garage.phoneTwo && (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Phone className="h-4 w-4" />
                        <span>{garage.phoneTwo}</span>
                      </div>
                    )}
                    {garage.email && (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Mail className="h-4 w-4" />
                        <span className="truncate">{garage.email}</span>
                      </div>
                    )}
                    {garage.workingHours && (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Clock className="h-4 w-4" />
                        <span>{garage.workingHours}</span>
                      </div>
                    )}
                    {garage.address && (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <MapPin className="h-4 w-4" />
                        <span className="truncate">{garage.address}</span>
                      </div>
                    )}
                    {garage.website && (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Globe className="h-4 w-4" />
                        <a 
                          href={garage.website.startsWith('http') ? garage.website : `https://${garage.website}`} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-blue-500 hover:underline truncate"
                        >
                          {garage.website}
                        </a>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2 pt-2">
                    <Button 
                      size="sm" 
                      className="flex-1"
                      onClick={() => handleCall(garage.phone)}
                    >
                      <Phone className="h-4 w-4 mr-1" />
                      Call
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => handleGetDirections(garage)}
                    >
                      <Navigation className="h-4 w-4 mr-1" />
                      Directions
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Pagination */}
        {!isLoading && totalPages > 1 && (
          <div className="flex justify-center items-center gap-4 mt-8">
            <Button
              variant="outline"
              onClick={() => setPage(prev => Math.max(1, prev - 1))}
              disabled={page === 1}
            >
              <ChevronLeft className="h-4 w-4 mr-1" /> Previous
            </Button>
            <span className="text-sm text-muted-foreground">
              Page {page} of {totalPages}
            </span>
            <Button
              variant="outline"
              onClick={() => setPage(prev => prev + 1)}
              disabled={page >= totalPages}
            >
              Next <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default MechanicGaragesPublicPage;
